// App state in one small store (same idea as Zustand: subscribe + selector hook).
// In the live app, session, subscription and progress come from the API; this store caches them.
import { useSyncExternalStore } from 'react';
import { load, remove, save } from './storage';
import type { AppNotification, Subscription } from './types';

export type VideoLang = 'Hindi' | 'English' | 'Both';
export type Quality = 'auto' | 'saver' | 'hd';

export interface Progress {
  showId: string;
  positionS: number;
  durationS: number;
  updatedAt: number;
}

export interface AppState {
  session: { userId: string; phone: string; name: string } | null;
  onboarded: boolean;
  interests: string[];
  videoLang: VideoLang;
  sub: Subscription | null;
  progress: Record<string, Progress>;
  likes: Record<string, true>;
  saves: Record<string, true>;
  readNotifs: Record<string, true>;
  extraNotifs: AppNotification[];
  /** Live API only: ids of the notifications the server returned (drives the unread badge). */
  notifIds?: string[];
  settings: {
    autoScroll: boolean;
    quality: Quality;
    textScale: 100 | 115 | 130;
    captions: boolean;
    push: boolean;
    theme: ThemePref;
  };
}

export type ThemePref = 'system' | 'light' | 'dark';

const KEY = 'kn-state-v1';

const initial: AppState = {
  session: null,
  onboarded: false,
  interests: [],
  videoLang: 'Both',
  sub: null,
  progress: {
    s3e3: { showId: 's3', positionS: 168, durationS: 240, updatedAt: Date.now() - 3 * 3600_000 },
    s2e5: { showId: 's2', positionS: 101, durationS: 290, updatedAt: Date.now() - 20 * 3600_000 },
  },
  likes: {},
  saves: {},
  readNotifs: {},
  extraNotifs: [],
  settings: { autoScroll: true, quality: 'auto', textScale: 100, captions: true, push: false, theme: 'system' },
};

let state: AppState = { ...initial, ...load<Partial<AppState>>(KEY, {}) };
state.settings = { ...initial.settings, ...state.settings };
const listeners = new Set<() => void>();

export function getState(): AppState {
  return state;
}

export function setState(patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)): void {
  const next = typeof patch === 'function' ? patch(state) : patch;
  state = { ...state, ...next };
  save(KEY, state);
  listeners.forEach((l) => l());
}

export function resetState(): void {
  remove(KEY);
  state = { ...initial, progress: {} };
  listeners.forEach((l) => l());
}

export function subscribeStore(l: () => void) { return subscribe(l); }

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useApp<T>(selector: (s: AppState) => T): T {
  return useSyncExternalStore(subscribe, () => selector(state), () => selector(state));
}

export function hasPlus(s: AppState = state): boolean {
  const sub = s.sub;
  if (!sub) return false;
  if (sub.status === 'trial' || sub.status === 'active') return true;
  return sub.status === 'cancelled' && sub.currentPeriodEnd > Date.now();
}

// ---- Toasts (not persisted) ----
let toastMsg: string | null = null;
let toastTimer: number | undefined;
const toastListeners = new Set<() => void>();
export function toast(msg: string): void {
  toastMsg = msg;
  toastListeners.forEach((l) => l());
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastMsg = null;
    toastListeners.forEach((l) => l());
  }, 2400);
}
export function useToast(): string | null {
  return useSyncExternalStore(
    (l) => { toastListeners.add(l); return () => toastListeners.delete(l); },
    () => toastMsg,
    () => toastMsg
  );
}
